import { apiClient } from '../../api/client';
import type { components } from '../../api/generated/schema';
import type { DraftFields } from '../../domain/drafts';
import { UnreadablePhotoError } from '../../domain/drafts';
import { isDomainError } from '../../domain/errors';
import { mapped } from '../errors';
import {
  toListing,
  toListingChanges,
  toNewListing,
  toPendingPhoto,
  toPhotos,
} from '../listings/mapping';
import type { DraftSyncTransport } from './DraftSyncTransport';

type S = components['schemas'];

const path = (id: string) => `/api/v1/listings/${encodeURIComponent(id)}/`;

/** A delete whose target is already gone has done its job. */
async function gone(call: () => Promise<unknown>): Promise<void> {
  try {
    await mapped(call);
  } catch (e) {
    if (!isDomainError(e, 'notFound')) throw e;
  }
}

/** Whether the browser can still read the start of a picked file. */
function readable(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(true);
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 1));
  });
}

function content(fields: DraftFields) {
  const { collectionId: _collection, ...rest } = fields;
  return toListingChanges(rest);
}

/** Draft sync over the app's one HTTP client and the listings mappings. */
export const httpDraftSyncTransport: DraftSyncTransport = {
  uploadPhoto: async (id, file, signal) => {
    // A picked file the browser can no longer read (moved, changed on disk)
    // would otherwise fail like a dropped connection and be retried forever.
    if (!(await readable(file))) throw new UnreadablePhotoError(id);
    return mapped(async () =>
      toPendingPhoto(await apiClient.pendingImageUpload(file, { id, signal })),
    );
  },
  createDraft: (id, fields, photoIds, signal) =>
    mapped(async () => {
      const body: S['ListingCreateRequest'] = {
        id,
        ...toNewListing({
          ...fields,
          publish: 'draft',
          publishAt: null,
          pendingPhotoIds: photoIds,
        }),
      };
      return toListing(
        await apiClient.send<S['Listing']>('POST', '/api/v1/listings/', body, signal),
      );
    }),
  patchContent: (listingId, fields, signal) =>
    mapped(async () =>
      toListing(
        await apiClient.send<S['Listing']>('PATCH', path(listingId), content(fields), signal),
      ),
    ),
  addPhotos: (listingId, photoIds, signal) =>
    mapped(async () =>
      toPhotos(
        await apiClient.send<S['Photo'][]>(
          'POST',
          `${path(listingId)}photos/`,
          { pending_image_ids: photoIds },
          signal,
        ),
      ),
    ),
  deletePhoto: (listingId, photoId, signal) =>
    gone(() =>
      apiClient.send(
        'DELETE',
        `${path(listingId)}photos/${encodeURIComponent(photoId)}/`,
        undefined,
        signal,
      ),
    ),
  reorderPhotos: (listingId, photoIds, signal) =>
    mapped(async () =>
      toPhotos(
        await apiClient.send<S['Photo'][]>(
          'PUT',
          `${path(listingId)}photos/order/`,
          { photo_ids: photoIds },
          signal,
        ),
      ),
    ),
  deleteListing: (id, signal) => gone(() => apiClient.send('DELETE', path(id), undefined, signal)),
  deletePendingImage: (id, signal) => gone(() => apiClient.pendingImageDelete(id, signal)),
};
